import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { FontVariantToken } from '@uipath/apollo-core';
import { ApButton } from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { FieldError } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type { ByoOperationGroupConfiguration } from '../../../../services/llm-gateway/LlmGateway.types';
import type { DeprecatedModelInfo } from '../../../../services/llm-gateway/LlmGateway.utils';
import { getModelConfiguration } from '../../../../services/llm-gateway/LlmGateway.utils';
import {
    LabelWithTooltip,
    type TooltipProps,
} from '../../subcomponents/LabelWithTooltip';

const useStyles = makeStyles(theme =>
    createStyles({
        wrapper: {
            display: 'flex',
            flexDirection: 'column',
            width: '350px',
        },
        triggerBox: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            border: '1px solid #526069',
            borderRadius: '4px',
            backgroundColor: theme.palette.semantic.colorBackground,
            cursor: 'pointer',
            width: '100%',
            height: '40px',
            boxSizing: 'border-box',
            '&:hover': { borderColor: theme.palette.primary.main },
        },
        triggerBoxOpen: {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
            padding: '7px 11px', // Compensate for thicker border
        },
        triggerBoxDisabled: {
            backgroundColor: theme.palette.action.disabledBackground,
            cursor: 'not-allowed',
            opacity: 0.6,
            '&:hover': { borderColor: '#526069' },
        },
        triggerBoxReadOnly: {
            cursor: 'default',
            backgroundColor: theme.palette.semantic.colorBackgroundDisabled,
            '&:hover': { borderColor: '#526069' },
        },
        searchRow: {
            padding: theme.spacing(0.5, 1),
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'center',
        },
        addButtonRow: {
            padding: theme.spacing(0.5, 1),
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
        dropdownPaper: {
            marginTop: theme.spacing(0.5),
            maxHeight: '280px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: theme.shadows[8],
        },
        optionsList: {
            overflow: 'auto',
            maxHeight: '250px',
        },
        sectionHeader: {
            padding: theme.spacing(1, 1.5),
            fontSize: '10px',
            fontWeight: 600,
            color: theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
        },
        optionItem: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: theme.spacing(0.75, 1.5),
            minHeight: '36px',
            cursor: 'pointer',
            '&:hover': { backgroundColor: theme.palette.action.hover },
        },
        optionItemSelected: { backgroundColor: theme.palette.action.selected },
        deprecatedBadge: {
            marginLeft: theme.spacing(1),
            padding: theme.spacing(0.25, 0.75),
            fontSize: '10px',
            fontWeight: 500,
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.action.hover,
            borderRadius: '12px',
            border: `1px solid ${theme.palette.divider}`,
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
        },
        label: {
            marginBottom: '4px',
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '16px',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        errorText: {
            color: theme.palette.error.main,
            marginTop: theme.spacing(0.5),
        },
        warningText: {
            color: theme.palette.error.main,
            marginTop: theme.spacing(0.5),
            fontSize: '12px',
        },
    }),
);

interface ModelOption {
    value: string;
    deprecated?: boolean;
}

interface ModelAlternativeDropdownProps {
    targetProduct: string;
    targetFeature: string;
    targetModel: string;
    value?: string;
    error?: FieldError;
    disabled?: boolean;
    alternatives?: string[];
    deprecatedModelsInfo?: Map<string, DeprecatedModelInfo>;
    productConfigurations: Record<string, Record<string, ByoOperationGroupConfiguration>>;
    onChange: (model: string, isCustomAlias: boolean) => void;
    onBlur?: () => void;
    labelTooltip?: TooltipProps;
}

export const ModelAlternativeDropdown: React.FC<ModelAlternativeDropdownProps> = ({
    targetProduct,
    targetFeature,
    targetModel,
    value,
    error,
    disabled,
    alternatives: overrideAlternatives,
    deprecatedModelsInfo,
    productConfigurations,
    onChange,
    onBlur,
    labelTooltip,
}) => {
    const { formatMessage: translate } = useIntl();
    const classes = useStyles();
    const anchorRef = useRef<HTMLDivElement>(null);
    const addInputRef = useRef<HTMLInputElement>(null);

    const [ open, setOpen ] = useState(false);
    const [ isAddMode, setIsAddMode ] = useState(false);
    const [ newItemValue, setNewItemValue ] = useState('');
    const [ customItems, setCustomItems ] = useState<string[]>([]);
    const [ existingCustomAlias, setExistingCustomAlias ] = useState<string | null>(null);

    const deprecatedModels = useMemo(() => {
        if (!deprecatedModelsInfo) {
            return [];
        }
        return Array.from(deprecatedModelsInfo.keys());
    }, [ deprecatedModelsInfo ]);

    const {
        preferredOptions, alternativeOptions, supportsGenericAlternative,
    } = useMemo(() => {
        const createOption = (model: string): ModelOption => ({
            value: model,
            deprecated: deprecatedModels.includes(model),
        });

        const sortOptions = (opts: ModelOption[]) => opts.sort((a, b) => {
            if (a.deprecated === b.deprecated) {
                return 0;
            }
            return a.deprecated ? 1 : -1;
        });

        if (overrideAlternatives) {
            return {
                preferredOptions: [ createOption(targetModel) ],
                alternativeOptions: sortOptions(overrideAlternatives.map(createOption)),
            };
        }

        const modelConfig = getModelConfiguration(productConfigurations, targetProduct, targetFeature, targetModel);
        const alternativesV2 = modelConfig?.alternativesWithProbes
            ?.map(alt => alt.model)
            .filter((model): model is string => Boolean(model));
        const alternatives = modelConfig?.alternatives ?? alternativesV2 ?? [];

        const uniqueAlternatives = [ ...new Set(alternatives) ].filter(alt => alt !== targetModel);

        return {
            preferredOptions: [ createOption(targetModel) ],
            alternativeOptions: sortOptions(uniqueAlternatives.map(createOption)),
            supportsGenericAlternative: (modelConfig?.genericAlternativeProbes ?? []).length > 0,
        };
    }, [ overrideAlternatives, productConfigurations, targetProduct, targetFeature, targetModel, deprecatedModels ]);

    const hasAlternatives = alternativeOptions.length > 0;
    const isReadOnly = !(hasAlternatives || supportsGenericAlternative);

    const selectedValue = value ?? targetModel;

    const errorMessage = useMemo(() => {
        if (!error?.message) {
            return undefined;
        }

        return error.message.split(',')[0];
    }, [ error?.message ]);

    useEffect(() => {
        if (value && !preferredOptions.concat(alternativeOptions).find(option => option.value === value)) {
            setCustomItems(prev => {
                if (prev.includes(value)) {
                    return prev;
                }
                return [ ...prev, value ];
            });
            // Track this as the existing custom alias
            setExistingCustomAlias(value);
        }
    }, [ value, preferredOptions, alternativeOptions ]);

    const handleOpen = useCallback(() => {
        if (!disabled && !isReadOnly) {
            setOpen(true);
            setIsAddMode(false);
            setNewItemValue('');
        }
    }, [ disabled, isReadOnly ]);

    const handleClose = useCallback(() => {
        setOpen(false);
        setIsAddMode(false);
        setNewItemValue('');
        onBlur?.();
    }, [ onBlur ]);

    const handleSelect = useCallback((optValue: string) => {
        onChange?.(optValue, false);
        handleClose();
    }, [ onChange, handleClose ]);

    const handleAddClick = useCallback(() => {
        setIsAddMode(true);
        // If editing an existing alias, pre-populate with the existing value
        setNewItemValue(existingCustomAlias ?? '');
    }, [ existingCustomAlias ]);

    const handleCancelAdd = useCallback(() => {
        setIsAddMode(false);
        setNewItemValue('');
    }, []);

    const handleConfirmAdd = useCallback(() => {
        const trimmedValue = newItemValue.trim();
        if (trimmedValue) {
            // If editing an existing alias, replace it (keep only one custom alias)
            if (existingCustomAlias) {
                setCustomItems(prev => {
                    // Remove the old alias and add the new one
                    const filtered = prev.filter(item => item !== existingCustomAlias);
                    return [ ...filtered, trimmedValue ];
                });
                setExistingCustomAlias(trimmedValue);
            } else {
                // Add to custom items if not already in options
                if (value !== trimmedValue && !customItems.includes(trimmedValue)) {
                    setCustomItems(prev => [ ...prev, trimmedValue ]);
                }
                setExistingCustomAlias(trimmedValue);
            }
            onChange(trimmedValue, true);
            handleClose();
        }
    }, [ newItemValue, onChange, handleClose, value, customItems, existingCustomAlias ]);

    const labelText = translate({ id: 'CLIENT_BYO_LLM_NAME' });

    const renderOption = (option: ModelOption) => (
        <Box
            key={option.value}
            onClick={() => handleSelect(option.value)}
            className={`${classes.optionItem} ${selectedValue === option.value ? classes.optionItemSelected : ''}`}
            data-cy={`model-alternative-option-${option.value}`}
        >
            <UiStack
                direction="row"
                align="center"
            >
                <UiText>
                    {option.value}
                </UiText>
                {option.deprecated && (
                    <span className={classes.deprecatedBadge}>
                        {translate({ id: 'CLIENT_BYO_LLM_DEPRECATED' })}
                    </span>
                )}
            </UiStack>
            {selectedValue === option.value && (
                <CheckIcon
                    sx={{
                        fontSize: 16,
                        color: 'primary.main',
                    }}
                />
            )}
        </Box>
    );

    return (
        <Box className={classes.wrapper}>
            {/* Label */}
            <LabelWithTooltip
                label={labelText}
                required
                tooltip={labelTooltip ?? {
                    translationKey: 'CLIENT_BYO_LLM_TOOLTIP_LLM_NAME',
                    placement: 'top',
                }}
                dataCy="model-alternative-dropdown-label-with-tooltip"
            />
            <Box
                ref={anchorRef}
                onClick={handleOpen}
                className={[
                    classes.triggerBox,
                    open && classes.triggerBoxOpen,
                    disabled && classes.triggerBoxDisabled,
                    isReadOnly && classes.triggerBoxReadOnly,
                ].filter(Boolean).join(' ')}
                data-cy="model-alternative-dropdown"
            >
                <UiText
                    variant={FontVariantToken.fontSizeM}
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {selectedValue}
                </UiText>
                {!isReadOnly && (
                    <KeyboardArrowDownIcon
                        sx={{
                            color: 'text.secondary',
                            transform: open ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s',
                        }}
                    />
                )}
            </Box>
            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                style={{
                    zIndex: 1300,
                    width: anchorRef.current?.offsetWidth,
                }}
            >
                <ClickAwayListener onClickAway={handleClose}>
                    <Paper className={classes.dropdownPaper}>
                        {supportsGenericAlternative ? (
                            <>
                                {isAddMode ? (
                                    <>
                                        {/* Row 1: Add input */}
                                        <Box className={classes.searchRow}>
                                            <InputBase
                                                inputRef={addInputRef}
                                                value={newItemValue}
                                                onChange={(e) => setNewItemValue(e.target.value)}
                                                placeholder={translate({ id: 'CLIENT_BYO_LLM_ENTER_CUSTOM_MODEL_NAME' })}
                                                fullWidth
                                                sx={{ fontSize: '0.875rem' }}
                                                data-cy="model-combobox-add-input"
                                            />
                                        </Box>

                                        {/* Row 2: Save/Cancel */}
                                        <Box>
                                            <UiStack
                                                direction="row"
                                                gap={SpacingToken.XS}
                                            >
                                                <ApButton
                                                    variant="primary"
                                                    size="small"
                                                    onClick={handleConfirmAdd}
                                                    disabled={!newItemValue.trim()}
                                                    data-cy="model-combobox-confirm-add"
                                                    label={existingCustomAlias ? translate({ id: 'CLIENT_SAVE' }) : translate({ id: 'CLIENT_BYO_LLM_ADD' })}
                                                />
                                                <ApButton
                                                    variant="tertiary"
                                                    size="small"
                                                    onClick={handleCancelAdd}
                                                    data-cy="model-combobox-cancel-add"
                                                    label={translate({ id: 'CLIENT_BYO_LLM_CANCEL' })}
                                                />
                                            </UiStack>
                                        </Box>
                                    </>
                                ) : (
                                    <>
                                        {/* Row 1: Add/Edit button */}
                                        <Box className={classes.addButtonRow}>
                                            <UiStack
                                                direction="row"
                                                align="center"
                                                gap={SpacingToken.XS}
                                                onClick={handleAddClick}
                                                data-cy="model-combobox-add-new"
                                            >
                                                {existingCustomAlias ? (
                                                    <EditIcon
                                                        sx={{
                                                            fontSize: 20,
                                                            color: 'primary.main',
                                                        }}
                                                    />
                                                ) : (
                                                    <AddIcon
                                                        sx={{
                                                            fontSize: 20,
                                                            color: 'primary.main',
                                                        }}
                                                    />
                                                )}
                                                <UiText
                                                    variant={FontVariantToken.fontSizeMBold}
                                                    color="var(--color-primary)"
                                                >
                                                    {
                                                        existingCustomAlias
                                                            ? translate({ id: 'CLIENT_BYO_LLM_EDIT_CUSTOM_ALIAS' })
                                                            : translate({ id: 'CLIENT_BYO_LLM_ADD_CUSTOM_ALIAS' })
                                                    }
                                                </UiText>
                                            </UiStack>
                                        </Box>

                                        {/* Options list */}
                                        <Box className={classes.optionsList}>
                                            {preferredOptions.map(renderOption)}
                                            {alternativeOptions.map(renderOption)}
                                        </Box>
                                    </>
                                )}
                            </>
                        ) : (
                            <Box className={classes.optionsList}>
                                <Box className={classes.sectionHeader}>
                                    {translate({ id: 'CLIENT_BYO_LLM_PREFERRED_SECTION' })}
                                </Box>
                                {preferredOptions.map(renderOption)}

                                <Box className={classes.sectionHeader}>
                                    {translate({ id: 'CLIENT_BYO_LLM_ALTERNATIVE_SECTION' })}
                                </Box>
                                {alternativeOptions.map(renderOption)}
                            </Box>
                        )}
                    </Paper>
                </ClickAwayListener>
            </Popper>

            {/* Error message */}
            {error && (
                <UiText
                    variant={FontVariantToken.fontSizeXsBold}
                    className={classes.errorText}
                >
                    {errorMessage}
                </UiText>
            )}
        </Box>
    );
};
